/**
 * What the two pickers offer a draft.
 *
 * Extracted from `useDraftProjectChoice` when the file cap gate bit. Both lists
 * come from the same three catalogues and both depend on the current selection,
 * so they are built together: choosing "no project" is what expands the
 * aggregate Cloud entry into one entry per sprite.
 */
import { useMemo } from "react";

import { useCmmdWorkIntakeSnapshot } from "../../cmmd/workIntakeResource";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { useStore } from "../../store";
import { selectArchivedCmmdProjectIds } from "../sidebar/archivedCmmdProjectIds";
import { resolveCloudProjectEnvironmentId } from "../sidebar/cloudProjectsGroup.logic";
import { cloudCatalogMembershipIsAuthoritative } from "../sidebar/connectedCloudProjects.logic";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";
import type { ExecutionOption } from "./composerChips";
import { buildDraftPickerProjectSummaries } from "./draftPickerProjectSummaries";
import { buildExecutionSummaries, type ProjectSummary } from "./NewThreadLanding.logic";
import type { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { useDesktopRuntimeCatalog } from "./useDesktopRuntimeCatalog";
import type { Project } from "../../types";

/**
 * What the two pickers offer.
 *
 * Both lists come from the same three catalogues, and both depend on the
 * current selection — choosing "no project" is what expands the aggregate Cloud
 * entry into one entry per sprite, so the selection has to be read before the
 * options are built rather than after.
 */
export function useDraftPickerOptions({
  activeEnvironmentId,
  cloudCatalog,
  desktopRuntimeCatalog,
  isCloudControlSurface,
  noWorkspaceSelected,
  projects,
  savedEnvironmentsById,
}: {
  readonly activeEnvironmentId: ReturnType<typeof useStore.getState>["activeEnvironmentId"];
  readonly cloudCatalog: ReturnType<typeof useCmmdForgeRuntimeListSnapshot>;
  readonly desktopRuntimeCatalog: ReturnType<typeof useDesktopRuntimeCatalog>;
  readonly isCloudControlSurface: boolean;
  readonly noWorkspaceSelected: boolean;
  readonly projects: readonly Project[];
  readonly savedEnvironmentsById: ReturnType<
    typeof useSavedEnvironmentRegistryStore.getState
  >["byId"];
}) {
  const resolveProjectDisplayName = useProjectDisplayNameResolver();
  // The same two facts the sidebar picker filters on, read here rather than
  // threaded through the caller: a picker that has to be HANDED the reasons a
  // project is unpickable is a picker that will one day be handed none.
  const cloudCatalogAuthoritative = cloudCatalogMembershipIsAuthoritative(cloudCatalog);
  const { projects: cmmdProjects } = useCmmdWorkIntakeSnapshot();
  const archivedCmmdProjectIds = useMemo(
    () => selectArchivedCmmdProjectIds(cmmdProjects),
    [cmmdProjects],
  );
  const executionOptions = useMemo<readonly ExecutionOption[]>(
    () =>
      buildExecutionSummaries({
        activeEnvironmentId,
        cloudCatalogRowCount: cloudCatalog.rows.length,
        isCloudControlSurface,
        desktopRuntimeInventory: desktopRuntimeCatalog.executionTargets,
        savedEnvironments: Object.values(savedEnvironmentsById).map((record) => ({
          environmentId: record.environmentId,
          label: record.label,
        })),
        cloudRuntimes: cloudCatalog.rows.map((row) => ({
          environmentId: resolveCloudProjectEnvironmentId(row),
          label: row.projectName,
          provisioned: row.runtimeId !== null,
        })),
        expandCloudRuntimes: noWorkspaceSelected,
      }),
    [
      activeEnvironmentId,
      cloudCatalog.rows,
      desktopRuntimeCatalog.executionTargets,
      isCloudControlSurface,
      noWorkspaceSelected,
      savedEnvironmentsById,
    ],
  );
  const projectSummaries = useMemo<ProjectSummary[]>(
    () =>
      buildDraftPickerProjectSummaries({
        projects,
        cloudCatalogRows: cloudCatalog.rows,
        cloudCatalogAuthoritative,
        archivedCmmdProjectIds,
        desktopInventoryProjects: desktopRuntimeCatalog.projects,
        resolveProjectDisplayName,
      }),
    [
      projects,
      cloudCatalog.rows,
      cloudCatalogAuthoritative,
      archivedCmmdProjectIds,
      desktopRuntimeCatalog.projects,
      resolveProjectDisplayName,
    ],
  );

  return { executionOptions, projectSummaries };
}
